import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Chip from '@material-ui/core/Chip';
import Avatar from '@material-ui/core/Avatar';
import Divider from '@material-ui/core/Divider';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
// import { useParams } from 'react-router-dom';
import CustomPropTypes from '../../helpers/CustomPropTypes';
import useStringTransforms from '../../hooks/useStringTransforms';
import useParams from '../../hooks/useParams'; // VS-PT-HACK
import ProfileIcon from '../../assets/sidebarIcons/Profile';

const useStyles = makeStyles((theme) => ({
  root: {
    overflowX: 'hidden',
  },
  title: {
    padding: theme.spacing(1, 0),
    fontSize: 15,
    fontWeight: '700',
    fontFamily: 'Avenir Next',
    textTransform: 'uppercase',
    color: theme.palette.common.black,
    lineHeight: '18px',
    letterSpacing: '0.04em',
    textAlign: 'left',
    // boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.12)',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  item: {
    padding: theme.spacing(1, 2),
    '&$active': {
      // backgroundColor: 'transparent',
      color: theme.palette.primary.main,
      backgroundColor: '#DBDBD1',
      '&:hover': {
        // backgroundColor: 'transparent',
        backgroundColor: '#DBDBD1',
      },
    },
  },
  active: {
    '& $text': {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    },
  },
  avatarWrapper: {
    minWidth: theme.spacing(5),
  },
  tinyAvatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    // backgroundColor: theme.palette.lightGrey.dark,
    backgroundColor: 'transparent',
    color: 'inherit',
    marginLeft: theme.spacing(1.875),
    marginRight: theme.spacing(1.5),
  },
  text: {
    fontSize: '0.775rem',
    overflow: 'hidden',
    wordBreak: 'break-word',
    WebkitLineClamp: 2,
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    color: theme.palette.darkGrey.light,
    fontWeight: 'bold',
  },
  badge: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    fontSize: 10,
    padding: 0,
    height: '10px',
    width: '10px',
  },
  divider: {
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(0, -3.5),
      height: theme.spacing(0.5),
      backgroundColor: 'rgba(123, 118, 118, 0.08)',
      width: 'calc(100% + 56px)',
      position: 'absolute',
    },
  },
}));

const ChannelList = ({
  title, channels, activeChannel, setActiveChannel, showAvatar, unreadMessages, newChannels,
  className, listClassName,
}) => {
  const classes = useStyles();
  const { toTitleCase } = useStringTransforms();
  const { t } = useTranslation('chat');
  const { locale } = useParams(); // VS-PT-HACK

  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const tablet = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

  return (
    <div className={clsx(classes.root, className)}>
      {(mobile || tablet) && !!title && (
        <>
          <Typography className={classes.title} gutterBottom>
            {title}
          </Typography>
          <Divider className={classes.divider} />
        </>
      )}
      <List
        className={listClassName}
        dense
        disablePadding
      >
        {channels.map((channel) => (
          <ListItem
            button
            disableGutters
            key={channel.topic}
            selected={channel.topic === activeChannel}
            onClick={() => setActiveChannel(channel.topic)}
            classes={{ root: classes.item, selected: classes.active }}
          >
            {(!!unreadMessages[channel.topic] || newChannels[channel.topic]) && (
              <Chip
                clickable
                className={classes.badge}
                size="small"
                label={unreadMessages[channel.topic] || t('channels.new')}
              />
            )}
            {showAvatar && (
              <ListItemAvatar className={classes.avatarWrapper}>
                <Avatar
                  src={channel.avatar}
                  className={classes.tinyAvatar}
                >
                  <ProfileIcon />
                </Avatar>
              </ListItemAvatar>
            )}
            <ListItemText
              classes={{ primary: classes.text }}
              primary={toTitleCase(typeof channel.name === 'string' ? channel.name
                : channel.name[locale || 'en'])}
            />
          </ListItem>
        ))}
      </List>
    </div>
  );
};

ChannelList.propTypes = {
  title: PropTypes.string,
  channels: PropTypes.arrayOf(CustomPropTypes.channel).isRequired,
  activeChannel: PropTypes.string,
  setActiveChannel: PropTypes.func.isRequired,
  showAvatar: PropTypes.bool,
  unreadMessages: PropTypes.objectOf(PropTypes.number).isRequired,
  newChannels: PropTypes.objectOf(PropTypes.bool).isRequired,
  className: PropTypes.string,
  listClassName: PropTypes.string,
};

ChannelList.defaultProps = {
  title: undefined,
  activeChannel: undefined,
  showAvatar: false,
  className: '',
  listClassName: '',
};

export default ChannelList;
